import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBasePath } from '../../lib/config/nav-helpers';
import { useTheme } from '../../lib/config/theme';
import { api } from '../../lib/api';
import { trackSearch, trackFeatureUse } from '../../lib/track-interaction';
import { HELP_CENTER_URL } from '../../lib/config/external-links';
import { useMe } from '../../lib/hooks/use-me';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Category = 'clients' | 'deals' | 'triggers' | 'actions' | 'commands';

interface PaletteItem {
  id: string;
  category: Category;
  title: string;
  subtitle?: string;
  shortcut?: string;
  action: (helpers: ActionHelpers) => void;
}

interface ActionHelpers {
  navigate: (path: string) => void;
  toggleTheme: () => void;
  openAgent: () => void;
  close: () => void;
}

/* ------------------------------------------------------------------ */
/*  Static items (always available)                                    */
/* ------------------------------------------------------------------ */

export function buildStaticItems(basePath: string, taxEnabled: boolean, partner: boolean): PaletteItem[] {
  const nav = (suffix: string) => (h: ActionHelpers) => { h.navigate(`${basePath}${suffix}`); h.close(); };

  const actions: PaletteItem[] = [
    { id: 'a1', category: 'actions', title: 'Go to Command Center', subtitle: 'Dashboard home', shortcut: '', action: nav('') },
    { id: 'a2', category: 'actions', title: 'Go to Finance', subtitle: 'Revenue analytics', action: nav('/revenue') },
    { id: 'a3', category: 'actions', title: 'Go to Clients', subtitle: 'Client management', action: nav('/clients') },
    { id: 'a4', category: 'actions', title: 'Go to Pipeline', subtitle: 'Deal pipeline', action: nav('/pipeline') },
    { id: 'a5', category: 'actions', title: 'Go to Intelligence', subtitle: 'Triggers & signals', action: nav('/intelligence') },
    { id: 'a6', category: 'actions', title: 'Go to Meetings', subtitle: 'Meeting prep & notes', action: nav('/meetings') },
    { id: 'a7', category: 'actions', title: 'Go to Actions', subtitle: 'Action inbox', action: nav('/actions') },
    { id: 'a8', category: 'actions', title: 'Go to Marketing', subtitle: 'Campaigns & social', action: nav('/marketing') },
    { id: 'a9', category: 'actions', title: 'Go to Sales', subtitle: 'Sales platform', action: (h) => { h.navigate('/sales'); h.close(); } },
    { id: 'a9c', category: 'actions', title: 'Go to Clients', subtitle: 'Client-management platform', action: (h) => { h.navigate('/clients'); h.close(); } },
    // Tax is a separately-sold add-on — only surface it for enabled workspaces.
    ...(taxEnabled
      ? [{ id: 'a9b', category: 'actions', title: 'Go to Tax', subtitle: 'Tax planning platform', action: (h: ActionHelpers) => { h.navigate('/tax'); h.close(); } } as PaletteItem]
      : []),
    { id: 'a10', category: 'actions', title: 'Go to Settings', subtitle: 'Preferences', action: nav('/settings') },
  ];

  const commands: PaletteItem[] = [
    { id: 'cmd1', category: 'commands', title: 'Toggle dark mode', subtitle: 'Switch light/dark theme', shortcut: 'T', action: (h) => { h.toggleTheme(); h.close(); } },
    { id: 'cmd2', category: 'commands', title: 'Open AI assistant', subtitle: 'Waypoint AI panel', shortcut: '/', action: (h) => { h.openAgent(); h.close(); } },
    { id: 'cmd3', category: 'commands', title: 'Keyboard shortcuts', subtitle: 'View all shortcuts', shortcut: '?', action: (h) => { window.dispatchEvent(new CustomEvent('waypoint:show-shortcuts')); h.close(); } },
    { id: 'cmd4', category: 'commands', title: 'Help Center', subtitle: 'Guides, answers & troubleshooting', action: (h) => { window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer'); h.close(); } },
  ];

  // A design-partner workspace has no Intelligence or Marketing hub to jump to.
  const hiddenForPartner = new Set(['a5', 'a8']);
  return [...(partner ? actions.filter(a => !hiddenForPartner.has(a.id)) : actions), ...commands];
}

/* ------------------------------------------------------------------ */
/*  Hook: fetch real clients + deals for the palette                   */
/* ------------------------------------------------------------------ */

function usePaletteData(isOpen: boolean, basePath: string, partner: boolean) {
  // Only fetch when the palette is open — lightweight and fast
  const clients = useQuery({
    queryKey: ['cmd-palette-clients'],
    queryFn: async () => {
      const res = await api<{ clients: Array<{ id: string; name: string; whyFlagged: string | null; revenueAnnual: number }> }>('/v1/client-health');
      return res.clients ?? [];
    },
    enabled: isOpen,
    staleTime: 60_000,
    retry: 0,
  });

  const deals = useQuery({
    queryKey: ['cmd-palette-deals'],
    queryFn: async () => {
      const res = await api<{ stages: Array<{ name: string; deals: Array<{ id: string; name: string; company: string; valueCents: number }> }> }>('/v1/sales/pipeline');
      return (res.stages ?? []).flatMap(s => s.deals.map(d => ({ ...d, stage: s.name })));
    },
    enabled: isOpen,
    staleTime: 60_000,
    retry: 0,
  });

  const triggers = useQuery({
    queryKey: ['cmd-palette-triggers'],
    queryFn: async () => {
      const res = await api<{ triggers: Array<{ id: string; headline: string; triggerType: string; significance: string }> }>('/v1/triggers');
      return (res.triggers ?? []).slice(0, 8);
    },
    enabled: isOpen && !partner,   // /v1/triggers is Intelligence's; a partner's API answers 403
    staleTime: 60_000,
    retry: 0,
  });

  return useMemo(() => {
    const nav = (suffix: string) => (h: ActionHelpers) => { h.navigate(`${basePath}${suffix}`); h.close(); };

    const clientItems: PaletteItem[] = (clients.data ?? []).map(c => ({
      id: `client-${c.id}`,
      category: 'clients' as const,
      title: c.name || 'Unnamed client',
      subtitle: [c.revenueAnnual ? `$${Math.round(c.revenueAnnual / 1000)}k rev` : null, c.whyFlagged].filter(Boolean).join(" · ") || "Client",
      action: nav(`/clients/${c.id}`),
    }));

    const dealItems: PaletteItem[] = (deals.data ?? []).map(d => ({
      id: `deal-${d.id}`,
      category: 'deals' as const,
      title: d.name || 'Untitled deal',
      subtitle: [d.company, d.valueCents ? `$${Math.round(d.valueCents / 100).toLocaleString()}` : null, d.stage].filter(Boolean).join(' · '),
      // Sales platform lives at top-level /sales (or /preview/sales) — not under /dashboard.
      action: (h: ActionHelpers) => { h.navigate(`${basePath === '/preview' ? '/preview' : ''}/sales/deals/${d.id}`); h.close(); },
    }));

    const triggerItems: PaletteItem[] = (triggers.data ?? []).map(t => ({
      id: `trigger-${t.id}`,
      category: 'triggers' as const,
      title: t.headline || 'Signal',
      subtitle: [(t.triggerType ?? '').replace(/_/g, ' '), t.significance].filter(Boolean).join(' · '),
      action: nav('/intelligence'),
    }));

    return [...clientItems, ...dealItems, ...triggerItems];
  }, [clients.data, deals.data, triggers.data, basePath]);
}

/* ------------------------------------------------------------------ */
/*  Fuzzy search helper                                                */
/* ------------------------------------------------------------------ */

function fuzzyMatch(text: string | null | undefined, query: string): boolean {
  // Null-safe: API rows can have a missing name/title; calling .toLowerCase on
  // undefined here threw and crashed the whole palette the instant you typed.
  const lower = (text ?? '').toLowerCase();
  const q = query.toLowerCase();
  // Each character of query must appear in order
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

function fuzzyScore(text: string | null | undefined, query: string): number {
  const lower = (text ?? '').toLowerCase();
  const q = query.toLowerCase();
  // Prefer starts-with, then includes, then fuzzy
  if (lower.startsWith(q)) return 3;
  if (lower.includes(q)) return 2;
  return 1;
}

/* ------------------------------------------------------------------ */
/*  Category metadata                                                  */
/* ------------------------------------------------------------------ */

const CATEGORY_META: Record<Category, { label: string; icon: string }> = {
  clients: {
    label: 'Clients',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  },
  deals: {
    label: 'Deals',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
  },
  triggers: {
    label: 'Triggers',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  actions: {
    label: 'Navigate',
    icon: 'M9 5l7 7-7 7',
  },
  commands: {
    label: 'Commands',
    icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
};

const CATEGORY_ORDER: Category[] = ['actions', 'commands', 'clients', 'deals', 'triggers'];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAgent: () => void;
}

export function CommandPalette({ isOpen, onClose, onOpenAgent }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const basePath = useBasePath();
  const me = useMe();
  const taxEnabled = me.features?.tax === true;

  const partner = me.features?.partner === true;
  const staticItems = useMemo(() => buildStaticItems(basePath, taxEnabled, partner), [basePath, taxEnabled, partner]);
  const liveItems = usePaletteData(isOpen, basePath, partner);
  const allItems = useMemo(() => [...liveItems, ...staticItems], [liveItems, staticItems]);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      // Small delay so the DOM is painted before focusing
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Filtered + grouped results
  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    return allItems
      .filter(item => fuzzyMatch(item.title, query) || (item.subtitle && fuzzyMatch(item.subtitle, query)))
      .sort((a, b) => fuzzyScore(b.title, query) - fuzzyScore(a.title, query));
  }, [query, allItems]);

  const grouped = useMemo(() => {
    const map = new Map<Category, PaletteItem[]>();
    for (const item of filtered) {
      const list = map.get(item.category) ?? [];
      list.push(item);
      map.set(item.category, list);
    }
    // Preserve category order
    const result: Array<{ category: Category; items: PaletteItem[] }> = [];
    for (const cat of CATEGORY_ORDER) {
      const items = map.get(cat);
      if (items && items.length > 0) result.push({ category: cat, items });
    }
    return result;
  }, [filtered]);

  // Flat list for arrow-key indexing
  const flatItems = useMemo(() => grouped.flatMap(g => g.items), [grouped]);

  // Clamp active index when results change
  useEffect(() => {
    setActiveIndex(idx => Math.min(idx, Math.max(0, flatItems.length - 1)));
  }, [flatItems.length]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const helpers: ActionHelpers = useMemo(() => ({
    navigate,
    toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    openAgent: onOpenAgent,
    close: onClose,
  }), [navigate, theme, setTheme, onOpenAgent, onClose]);

  const executeItem = useCallback((item: PaletteItem) => {
    if (query.trim()) trackSearch(query.trim(), filtered.length);
    trackFeatureUse('command_palette_select', { item_id: item.id, category: item.category });
    item.action(helpers);
  }, [helpers, query, filtered.length]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(i => (i + 1) % Math.max(1, flatItems.length));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => (i - 1 + flatItems.length) % Math.max(1, flatItems.length));
        break;
      case 'Enter':
        e.preventDefault();
        if (flatItems[activeIndex]) executeItem(flatItems[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [flatItems, activeIndex, executeItem, onClose]);

  if (!isOpen) return null;

  let runningIndex = 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-toast bg-black/50 backdrop-blur-xs animate-fade-in"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-61 flex items-start justify-center pt-[15vh] pointer-events-none">
        <div
          className="pointer-events-auto w-full max-w-xl rounded-xl border border-border shadow-2xl glass"
          style={{ animation: 'cmdFadeIn 120ms ease-out' }}
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-ink-dim">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
              placeholder="Search clients, deals, triggers, or type a command..."
              className="flex-1 bg-transparent text-sm text-ink placeholder:text-ink-dim focus:outline-hidden"
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              data-form-type="other"
            />
            <kbd className="hidden sm:inline-flex items-center rounded-sm border border-border px-1.5 py-0.5 text-2xs font-mono text-ink-dim">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto overscroll-contain py-2">
            {flatItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-ink-dim">
                No results for &ldquo;{query}&rdquo;
              </div>
            ) : (
              grouped.map(group => {
                const meta = CATEGORY_META[group.category];
                const groupJsx = (
                  <div key={group.category}>
                    <div className="px-4 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-ink-dim">
                      {meta.label}
                    </div>
                    {group.items.map(item => {
                      const idx = runningIndex++;
                      const isActive = idx === activeIndex;
                      return (
                        <button
                          key={item.id}
                          data-active={isActive}
                          className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
                            isActive ? 'bg-primary/10 text-ink' : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                          }`}
                          onClick={() => executeItem(item)}
                          onMouseEnter={() => setActiveIndex(idx)}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="shrink-0"
                          >
                            <path d={meta.icon} />
                          </svg>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-medium">{item.title}</div>
                            {item.subtitle && (
                              <div className="truncate text-2xs text-ink-dim">{item.subtitle}</div>
                            )}
                          </div>
                          {item.shortcut && (
                            <kbd className="hidden sm:inline-flex shrink-0 items-center rounded-sm border border-border px-1.5 py-0.5 text-2xs font-mono text-ink-dim">
                              {item.shortcut}
                            </kbd>
                          )}
                          {isActive && (
                            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-ink-dim">
                              <polyline points="9 10 4 15 9 20" />
                              <path d="M20 4v7a4 4 0 0 1-4 4H4" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
                return groupJsx;
              })
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border px-4 py-2">
            <div className="flex items-center gap-3 text-2xs text-ink-dim">
              <span className="flex items-center gap-1">
                <kbd className="rounded-sm border border-border px-1 py-0.5 font-mono text-2xs">&uarr;</kbd>
                <kbd className="rounded-sm border border-border px-1 py-0.5 font-mono text-2xs">&darr;</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded-sm border border-border px-1 py-0.5 font-mono text-2xs">&crarr;</kbd>
                select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded-sm border border-border px-1 py-0.5 font-mono text-2xs">esc</kbd>
                close
              </span>
            </div>
            <span className="text-2xs text-ink-dim">{flatItems.length} results</span>
          </div>
        </div>
      </div>

      {/* Inline keyframe for the dialog entrance */}
      <style>{`
        @keyframes cmdFadeIn {
          from { opacity: 0; transform: scale(0.97) translateY(-8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
      `}</style>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Hook: global Cmd+K / Ctrl+K listener                               */
/* ------------------------------------------------------------------ */

export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, open, close };
}
